import React, { useEffect, useState } from "react";
import StoreAPI from "../../../api/StoreApi";
import { showSuccess, showError } from "../../../utils/toastUtils";

function ShippingSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [shippingGateways, setShippingGateways] = useState({
    shiprocket: {
      enabled: false,
      config: {
        email: "",
        password: "",
        mode: "",
        pickupAddressId: "",
      },
    },
    shipmozo: {
      enabled: false,
      config: {
        public_key: "",
        private_key: "",
      },
    },
    icarry: {
      enabled: false,
      config: {
        apiKey: "",
        username: "",
      },
    },
    velocity: {
      enabled: false,
      config: {
        bearerToken: "",
      },
    },
    fedex: {
      enabled: false,
      config: {
        apiKey: "",
        secretKey: "",
        accountNumber: "",
      },
    },
  });

  const handleToggle = (providerName) => {
    setShippingGateways((prev) => ({
      ...prev,
      [providerName]: {
        ...prev[providerName],
        enabled: !prev[providerName].enabled,
      },
    }));
  };

  const handleChange = (gateway, field, value) => {
    if (field === "config") {
      setShippingGateways((prev) => ({
        ...prev,
        [gateway]: {
          ...prev[gateway],
          config: value,
        },
      }));
    }
  };

  useEffect(() => {
    fetchGatewayDetails();
  }, []);

  const fetchGatewayDetails = async () => {
    try {
      const res = await StoreAPI.shippingGatewayDetails();

      if (res.code === 200) {
        const formatted = {};

        res.data.forEach((g) => {
          formatted[g.providerName] = {
            enabled: !g.isDeleted,
            config: {
              ...shippingGateways[g.providerName]?.config,
              ...g.config,
            },
          };
        });

        setShippingGateways((prev) => ({
          ...prev,
          ...formatted,
        }));
      }
    } catch (err) {}
  };

  const validate = () => {
    const newErrors = {};
    const enabled = Object.entries(shippingGateways).filter(
      ([_, g]) => g.enabled,
    );

    if (!enabled.length) {
      newErrors.general = "At least one shipping gateway must be enabled.";
    }

    enabled.forEach(([key, g]) => {
      const s = g.config;

      if (key === "shiprocket") {
        if (!s.email) newErrors.shiprocketEmail = "Email is required.";
        if (!s.password) newErrors.shiprocketPassword = "Password is required.";
      }

      if (key === "shipmozo") {
        if (!s.public_key) newErrors.shipmozoPublic = "Public Key is required.";
        if (!s.private_key) newErrors.shipmozoPrivate = "Private Key is required.";
      }

      if (key === "icarry") {
        if (!s.username) newErrors.icarryUsername = "API Username is required.";
        if (!s.apiKey) newErrors.icarryApiKey = "API Token is required.";
      }

      if (key === "velocity") {
        if (!s.bearerToken) newErrors.velocityToken = "Bearer Token is required.";
      }

      if (key === "fedex") {
        if (!s.apiKey) newErrors.fedexApiKey = "FedEx API Key is required.";
        if (!s.secretKey) newErrors.fedexSecretKey = "FedEx Secret Key is required.";
        if (!s.accountNumber) newErrors.fedexAccountNumber = "FedEx Account Number is required.";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = Object.entries(shippingGateways)
        .filter(([providerName]) => providerName !== undefined && providerName !== "")
        .map(([providerName, g]) => ({
          providerName,
          isDeleted: !g.enabled,
          enabled: g.enabled,
          config: g.config,
        }));

      const res = await StoreAPI.updateShippingGatewayDetails({
        providers: payload,
      });

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Shipping Gateway Settings</h4>
      </div>
      <div className="card-body shipment">
        <form method="post" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="col-12 mb-3">
              <div className="alert alert-danger py-2">{errors.general}</div>
            </div>
          )}
          
          {/* Shiprocket Section */}
          <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <span className="system-app-icon">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/icons/shiprocket.png`}
                          alt="Shiprocket"
                        />
                      </span>
                      <div className="security-title">
                        <h5 className="fs-16 fw-medium">Ship Rocket</h5>
                      </div>
                    </div>
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center ms-2">
                      <input
                        type="checkbox"
                        id="shiprocket-toggle"
                        className="check"
                        checked={!!shippingGateways?.shiprocket?.enabled}
                        onChange={() => handleToggle("shiprocket")}
                      />
                      <label htmlFor="shiprocket-toggle" className="checktoggle"></label>
                    </div>
                  </div>
                  <p className="fs-14 mb-3">Enable Ship Rocket for shipping integration.</p>
                  {shippingGateways?.shiprocket?.enabled && (
                    <>
                      <div className="alert alert-light border">
                        <h6 className="fw-semibold mb-2">Setup Instructions</h6>
                        <ol className="ps-3 mb-0">
                          <li>Create your account on <a href="https://www.shiprocket.in" target="_blank" rel="noopener noreferrer">shiprocket.in</a></li>
                          <li>Enter your registered email and password below.</li>
                        </ol>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">Email Address <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Email Address"
                              value={shippingGateways.shiprocket.config.email}
                              onChange={(e) =>
                                handleChange("shiprocket", "config", {
                                  ...shippingGateways.shiprocket.config,
                                  email: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">Password <span className="text-danger">*</span></label>
                            <input
                              type="password"
                              className="form-control"
                              placeholder="Enter Password"
                              value={shippingGateways.shiprocket.config.password}
                              onChange={(e) =>
                                handleChange("shiprocket", "config", {
                                  ...shippingGateways.shiprocket.config,
                                  password: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Shipmozo Section */}
          <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <span className="system-app-icon">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/icons/shipmozo.png`}
                          alt="Shipmozo"
                        />
                      </span>
                      <div className="security-title">
                        <h5 className="fs-16 fw-medium">Shipmozo</h5>
                      </div>
                    </div>
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center ms-2">
                      <input
                        type="checkbox"
                        id="shipmozo-toggle"
                        className="check"
                        checked={!!shippingGateways?.shipmozo?.enabled}
                        onChange={() => handleToggle("shipmozo")}
                      />
                      <label htmlFor="shipmozo-toggle" className="checktoggle"></label>
                    </div>
                  </div>
                  <p className="fs-14 mb-3">Enable Shipmozo for shipping integration.</p>
                  {shippingGateways?.shipmozo?.enabled && (
                    <>
                      <div className="alert alert-light border mb-3">
                        <h6 className="fw-semibold mb-2">Setup Instructions</h6>
                        <ol className="mb-0 ps-3">
                          <li>Register on <a href="https://shipmozo.com" target="_blank" rel="noopener noreferrer">shipmozo.com</a></li>
                          <li>Copy Public Key and Private Key and paste them below.</li>
                        </ol>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">Public Key <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Public Key"
                              value={shippingGateways.shipmozo.config.public_key}
                              onChange={(e) =>
                                handleChange("shipmozo", "config", {
                                  ...shippingGateways.shipmozo.config,
                                  public_key: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <label className="form-label">Private Key <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Private Key"
                              value={shippingGateways.shipmozo.config.private_key}
                              onChange={(e) =>
                                handleChange("shipmozo", "config", {
                                  ...shippingGateways.shipmozo.config,
                                  private_key: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* iCarry Section */}
          <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <span className="system-app-icon">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/icons/iCarry.png`}
                          alt="iCarry"
                        />
                      </span>
                      <div className="security-title">
                        <h5 className="fs-16 fw-medium">iCarry.in</h5>
                      </div>
                    </div>
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center ms-2">
                      <input
                        type="checkbox"
                        id="icarry-toggle"
                        className="check"
                        checked={!!shippingGateways?.icarry?.enabled}
                        onChange={() => handleToggle("icarry")}
                      />
                      <label htmlFor="icarry-toggle" className="checktoggle"></label>
                    </div>
                  </div>
                  <p className="fs-14 mb-3">Enable icarry for shipping integration.</p>
                  {shippingGateways?.icarry?.enabled && (
                    <>
                      <div className="alert alert-light border mb-3">
                        <h6 className="fw-semibold mb-2">Setup Instructions</h6>
                        <ol className="mb-0 ps-3">
                          <li>Register on <a href="https://icarry.in" target="_blank" rel="noopener noreferrer">icarry.in</a></li>
                          <li>Copy API Username and API Token and paste them below.</li>
                        </ol>
                      </div>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-2">
                            <label className="form-label">API Username <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter API Username"
                              value={shippingGateways.icarry.config.username}
                              onChange={(e) =>
                                handleChange("icarry", "config", {
                                  ...shippingGateways.icarry.config,
                                  username: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="mb-2">
                            <label className="form-label">API Token <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter API Token"
                              value={shippingGateways.icarry.config.apiKey}
                              onChange={(e) =>
                                handleChange("icarry", "config", {
                                  ...shippingGateways.icarry.config,
                                  apiKey: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Velocity Section */}
          <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <span className="system-app-icon">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/icons/velocity.jpg`}
                          alt="Velocity"
                        />
                      </span>
                      <div className="security-title">
                        <h5 className="fs-16 fw-medium">Velocity</h5>
                      </div>
                    </div>
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center ms-2">
                      <input
                        type="checkbox"
                        id="velocity-toggle"
                        className="check"
                        checked={!!shippingGateways?.velocity?.enabled}
                        onChange={() => handleToggle("velocity")}
                      />
                      <label htmlFor="velocity-toggle" className="checktoggle"></label>
                    </div>
                  </div>
                  <p className="fs-14 mb-3">Enable Velocity for shipping integration.</p>
                  {shippingGateways?.velocity?.enabled && (
                    <>
                      <div className="alert alert-light border mb-3">
                        <h6 className="fw-semibold mb-2">Setup Instructions</h6>
                        <ol className="mb-0 ps-3">
                          <li>Generate or copy your **Bearer Token** from your Velocity dashboard.</li>
                        </ol>
                      </div>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-2">
                            <label className="form-label">Bearer Token <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Bearer Token"
                              value={shippingGateways.velocity.config.bearerToken}
                              onChange={(e) =>
                                handleChange("velocity", "config", {
                                  ...shippingGateways.velocity.config,
                                  bearerToken: e.target.value,
                                })
                              }
                            />
                            {errors.velocityToken && (
                              <span className="text-danger fs-12">{errors.velocityToken}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <span className="system-app-icon">
                        <img
                          src={`${process.env.PUBLIC_URL}/img/icons/fedex.png`}
                          alt="FedEx"
                        />
                      </span>
                      <div className="security-title">
                        <h5 className="fs-16 fw-medium">FedEx</h5>
                      </div>
                    </div>
                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center ms-2">
                      <input
                        type="checkbox"
                        id="fedex-toggle"
                        className="check"
                        checked={!!shippingGateways?.fedex?.enabled}
                        onChange={() => handleToggle("fedex")}
                      />
                      <label htmlFor="fedex-toggle" className="checktoggle"></label>
                    </div>
                  </div>
                  <p className="fs-14 mb-3">Enable FedEx for global shipping integration.</p>
                  {shippingGateways?.fedex?.enabled && (
                    <>
                      <div className="alert alert-light border mb-3">
                        <h6 className="fw-semibold mb-2">Setup Instructions</h6>
                        <ol className="mb-0 ps-3">
                          <li>Create/Log in to your account on <a href="https://www.fedex.com" target="_blank" rel="noopener noreferrer">fedex.com</a></li>
                          <li>Navigate to the Developer Portal to generate your *API Key* and *Secret Key*.</li>
                          <li>Enter your FedEx *Account Number*, API Key, and Secret Key below.</li>
                        </ol>
                      </div>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label">Account Number <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter FedEx Account Number"
                              value={shippingGateways.fedex.config.accountNumber}
                              onChange={(e) =>
                                handleChange("fedex", "config", {
                                  ...shippingGateways.fedex.config,
                                  accountNumber: e.target.value,
                                })
                              }
                            />
                            {errors.fedexAccountNumber && (
                              <span className="text-danger fs-12">{errors.fedexAccountNumber}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label">API Key <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter API Key"
                              value={shippingGateways.fedex.config.apiKey}
                              onChange={(e) =>
                                handleChange("fedex", "config", {
                                  ...shippingGateways.fedex.config,
                                  apiKey: e.target.value,
                                })
                              }
                            />
                            {errors.fedexApiKey && (
                              <span className="text-danger fs-12">{errors.fedexApiKey}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label">Secret Key <span className="text-danger">*</span></label>
                            <input
                              type="password"
                              className="form-control"
                              placeholder="Enter Secret Key"
                              value={shippingGateways.fedex.config.secretKey}
                              onChange={(e) =>
                                handleChange("fedex", "config", {
                                  ...shippingGateways.fedex.config,
                                  secretKey: e.target.value,
                                })
                              }
                            />
                            {errors.fedexSecretKey && (
                              <span className="text-danger fs-12">{errors.fedexSecretKey}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div> */}

          <div className="row">
            <div className="col-12 d-flex justify-content-end">
              {!isLoading && (
                <button type="submit" className="btn btn-primary shadow-none">
                  Submit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default ShippingSettings;